import React, { useState } from 'react';
import { CalendarDays, SlidersHorizontal } from 'lucide-react';
import clsx from 'clsx';
import DateRangeFilter from '@/components/filters/DateRangeFilter';
import { Tab } from '@headlessui/react';

export interface TransactionFilterValues {
  merchantName: string;
  category: string;
  startDate: string;
  endDate: string;
}

interface Props {
  filters: TransactionFilterValues;
  onChange: (filters: TransactionFilterValues) => void;
  onApply: () => void;
  onClear: () => void;
}

const TransactionFilters: React.FC<Props> = ({ filters, onChange, onApply, onClear }) => {
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [showTabFilter, setShowTabFilter] = useState(false);

  return (
    <div className="w-full relative space-y-4">
      {/* Top-Right Buttons */}
      <div className="flex justify-end gap-2">
        {/* 📅 Date Button */}
        <button
         onClick={() => {
  setShowDateFilter((prev) => !prev);
  setShowTabFilter(false); // close filter panel
}}
          className="flex items-center gap-2 px-3 py-2 border rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-white"
        >
          <CalendarDays className="h-4 w-4" />
          Date
        </button>

        {/* 🧩 Filter Button */}
        <button
          onClick={() => {
  setShowTabFilter((prev) => !prev);
  setShowDateFilter(false); // close date panel
}}
          className="flex items-center gap-2 px-3 py-2 border rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-white"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filter
        </button>
      </div>

      {/* 📅 Floating Date Filter Panel */}
      {showDateFilter && (
        <div className="absolute right-0 z-10 mt-2">
          <DateRangeFilter
            startDate={filters.startDate}
            endDate={filters.endDate}
            onApply={(start, end) => {
              onChange({ ...filters, startDate: start, endDate: end });
              setShowDateFilter(false);
              onApply();
            }}
            onClear={() => {
              onChange({ ...filters, startDate: '', endDate: '' });
              setShowDateFilter(false);
              onApply();
            }}
            onCancel={() => setShowDateFilter(false)}
          />
        </div>
      )}

      {/* 🧩 Tabbed Filter Panel */}
      {showTabFilter && (
        <div className="absolute right-0 z-20 mt-2 w-[400px] bg-white dark:bg-gray-900 border rounded-lg shadow-md p-4">
          <Tab.Group>
            <Tab.List className="flex space-x-2 border-b mb-4">
              <Tab className={({ selected }) =>
                clsx(
                  'px-3 py-1 text-sm border-b-2',
                  selected
                    ? 'text-emerald-600 border-emerald-500 font-semibold'
                    : 'text-gray-500 border-transparent'
                )}>
                Category
              </Tab>
              <Tab className={({ selected }) =>
                clsx(
                  'px-3 py-1 text-sm border-b-2',
                  selected
                    ? 'text-emerald-600 border-emerald-500 font-semibold'
                    : 'text-gray-500 border-transparent'
                )}>
                Merchant
              </Tab>
            </Tab.List>

            <Tab.Panels>
              <Tab.Panel>
                <input
                  type="text"
                  value={filters.category}
                  onChange={(e) => onChange({ ...filters, category: e.target.value })}
                  placeholder="Enter category"
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </Tab.Panel>
              <Tab.Panel>
                <input
                  type="text"
                  value={filters.merchantName}
                  onChange={(e) => onChange({ ...filters, merchantName: e.target.value })}
                  placeholder="Enter merchant name"
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>

          {/* Bottom Buttons */}
          <div className="flex justify-between mt-4">
            <button
              className="px-4 py-1 border rounded text-sm text-gray-600 hover:bg-gray-100"
              onClick={() => {
                onChange({ ...filters, category: '', merchantName: '' });
                setShowTabFilter(false);
                onApply();
              }}
            >
              Clear
            </button>
            <div className="flex gap-2">
              <button
                className="px-4 py-1 border rounded text-sm text-gray-600 hover:bg-gray-100"
                onClick={() => setShowTabFilter(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-1 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700"
                onClick={() => {
                  setShowTabFilter(false);
                  onApply();
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionFilters;
